import { useAiReceptionistAccess } from '@frontend/ai-receptionist-settings';
import { useHasAdminAccess } from '@frontend/analytics-shared';
import { SOCIAL_LINKS_SECTION_ID, useHasAccess as useHasBrandSettingsAccess } from '@frontend/brand-settings';
import { useBulkMessagingAcls } from '@frontend/bulk-messaging';
import { useHasCallIntelTaskSettingAccess } from '@frontend/call-intel-settings';
import { useTranslation } from '@frontend/i18n';
import { useInboxMutePolicy } from '@frontend/inbox-mute-policy';
import { useHasInsuranceVerificationACLAccess, useHasWIVOrEIVAccess } from '@frontend/insurance-verification';
import { useMessagingFeaturesEnabled } from '@frontend/messaging-enabled-hook';
import { NOT_SUPPORTED_IN_SCOPE_MESSAGE, useScopePolicy } from '@frontend/scope-access';
import { SettingsRoutes, StaticSettingsRoutes } from '@frontend/settings-routing';
import { useIsSubscriptionSettingsEnabled } from '@frontend/subscription';

export type AllPathsMap = Record<
  SettingsRoutes,
  { label: string; show?: boolean; redirectTo?: StaticSettingsRoutes; redirectSearch?: Record<string, string> }
>;
type NavItemsPaths = SettingsRoutes;

type NavItem = {
  label: string;
  path: NavItemsPaths;
  show?: boolean;
  disabledReason?: string;
  redirectTo?: StaticSettingsRoutes;
  redirectSearch?: Record<string, string>;
  hasBottomSpacing?: boolean;
  description?: string;
};

function useSettingsNavItems() {
  const { t } = useTranslation('settingsNav');
  const { hasInsuranceVerificationAccess, isDental } = useHasWIVOrEIVAccess();
  const { hasAccessToInsuranceVerification: hasACLAccess } = useHasInsuranceVerificationACLAccess();
  const { hasDomainVerifyAccess } = useBulkMessagingAcls();
  const hasAnalyticsAdminAccess = useHasAdminAccess();
  const { hasAccess: hasBrandSettingsAccess } = useHasBrandSettingsAccess();
  const isSubscriptionSettingsEnabled = useIsSubscriptionSettingsEnabled();
  const { hasAccess: hasCallIntelTaskSettingAccess } = useHasCallIntelTaskSettingAccess();
  const { getDecisionForPath } = useScopePolicy();
  const { isHidden } = useInboxMutePolicy();
  const { hasAiReceptionistAccess } = useAiReceptionistAccess();
  const hasAISettingsAccess = hasCallIntelTaskSettingAccess || hasAiReceptionistAccess;
  const messagingEnabled = useMessagingFeaturesEnabled();

  const navItems: NavItem[] = [
    {
      path: '/analytics',
      label: t('Analytics') /* AUTO-GENERATED */,
      description:
        'High-level analytics dashboard providing snapshots of calls, messages, appointments, and other KPIs.' /* AUTO-GENERATED */,
      show: hasAnalyticsAdminAccess,
    },
    {
      path: '/brand',
      label: t('Brand') /* AUTO-GENERATED */,
      description:
        'User can manage or configure branding settings for their Weave account or services from this page.' /* AUTO-GENERATED */,
      show: hasBrandSettingsAccess,
    },
    {
      path: '/digital-forms',
      label: t('Forms') /* AUTO-GENERATED */,
      description:
        'Shortcut to the digital forms module. View, create, or manage practice forms in a centralized place.' /* AUTO-GENERATED */,
    },
    {
      path: '/email',
      label: t('Email') /* AUTO-GENERATED */,
      description:
        'Email marketing settings. Manage email addresses and domains used with email marketing.' /* AUTO-GENERATED */,
      show: hasDomainVerifyAccess,
    },
    {
      path: '/fax',
      label: t('Fax') /* AUTO-GENERATED */,
      description:
        'Primary fax module overview, providing quick access to inbox, drafts, and fax configuration.' /* AUTO-GENERATED */,
    },
    {
      path: '/image-gallery',
      label: t('Image Gallery') /* AUTO-GENERATED */,
      description:
        'Manage practice images and media assets used across various communications and forms.' /* AUTO-GENERATED */,
    },
    {
      path: '/insurance',
      label: t('Insurance') /* AUTO-GENERATED */,
      description: 'Manage insurance verification settings and payer connection configurations.' /* AUTO-GENERATED */,
      show: isDental && hasInsuranceVerificationAccess && hasACLAccess, // Note: Remove the isDental check when WIV for non dental locations is supported
    },
    {
      path: '/messaging',
      label: t('Messaging') /* AUTO-GENERATED */,
      description:
        'Primary messaging module overview. May contain shortcuts to inbox, archived messages, and templates.' /* AUTO-GENERATED */,
      show: messagingEnabled,
      disabledReason: isHidden('settings-messaging')
        ? t('Your inbox is muted. Unmute to access this setting.')
        : undefined,
    },
    {
      path: '/payments',
      label: t('Payments') /* AUTO-GENERATED */,
      description:
        'Main payments overview page. Access invoices, statements, settings, and payment-related features.' /* AUTO-GENERATED */,
    },
    {
      path: '/phone/main',
      label: t('Phone') /* AUTO-GENERATED */,
      description: 'Configure main phone system settings including general telephony preferences.' /* AUTO-GENERATED */,
    },
    {
      path: '/reviews',
      label: t('Social') /* AUTO-GENERATED */,
      description: 'User can manage settings related to reviews from this page.' /* AUTO-GENERATED */,
      show: !hasBrandSettingsAccess, // Hide Social when Brand Settings is available
      ...(hasBrandSettingsAccess
        ? {
            redirectTo: '/brand' as const,
            redirectSearch: { scrollTo: SOCIAL_LINKS_SECTION_ID },
          }
        : {}),
    },
    {
      path: '/schedule',
      label: t('Schedule') /* AUTO-GENERATED */,
      description: 'Primary scheduling hub, linking to calendar, appointment' /* AUTO-GENERATED */,
      hasBottomSpacing: true,
    },
    {
      path: '/ai-configuration/main',
      label: t('AI Settings') /* AUTO-GENERATED */,
      description: 'Manage and configure your AI-powered tools and features.' /* AUTO-GENERATED */,
      show: hasAISettingsAccess,
    },
    {
      path: '/subscriptions',
      label: t('Subscriptions') /* AUTO-GENERATED */,
      description:
        'View and manage your current subscriptions, billing, and feature access levels.' /* AUTO-GENERATED */,
      show: isSubscriptionSettingsEnabled,
    },
    {
      path: '/tags',
      label: t('Tags') /* AUTO-GENERATED */,
      description:
        'User can manage the tags from this link, which likely includes creating, editing, or deleting tags used for categorizing contacts or communications.' /* AUTO-GENERATED */,
      hasBottomSpacing: true,
      disabledReason: isHidden('settings-tags') ? t('Your inbox is muted. Unmute to access this setting.') : undefined,
    },
  ];

  return navItems.map((item) => {
    const decision = getDecisionForPath(`/settings${item.path}`);
    const isBlockedByScopePolicy = decision.availability !== 'available';

    return {
      ...item,
      show: item.show,
      disabledReason: isBlockedByScopePolicy ? NOT_SUPPORTED_IN_SCOPE_MESSAGE : item.disabledReason,
    };
  });
}

export function useHidePickerConditions(): ((matches: { pathname: string; id: string }[]) => boolean)[] {
  return [];
}

export { useSettingsNavItems };
